import './styles/ManageJobOfferItem.scss';

import { Menu } from '@mui/icons-material';
import { Grid, Paper, Fab, MenuItem } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import clsx from 'clsx';
import { useRef, FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { useJobOfferingsMutation } from 'api/hooks/jobmarket/offers';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import ContextMenu from 'app/components/ContextMenu';
import { FormWrapperModal } from 'app/components/FormWrapperModal';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { ManageOffer } from 'app/containers/Jobmarket/ManageOffer';
import { translations } from 'locales/i18n';
import { JobOffering } from 'types/AppTypes';

import { LabeledInput } from '../CompanyItem/components/LabeledInput';

interface Props {
  offer: JobOffering;
}

export const ManageJobOfferItem: FunctionComponent<Props> = ({ offer }) => {
  const { t } = useTranslation();
  const [EditViewOpen, setEditViewOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const anchorRef = useRef(null);

  const { deletion } = useJobOfferingsMutation();

  const dispatch = useDispatch();
  const handleDelete = async () => {
    try {
      await deletion.mutateAsync(offer.id, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['companies'] });
          queryClient.invalidateQueries({ queryKey: ['jobOfferings'] });
        }
      });
      dispatch(
        AlertActions.success(t(translations.pages.manageJobOffers.alerts.deleteSuccessful()))
      );
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.manageJobOffers.alerts.deleteFailed())));
    }
  };

  return (
    <>
      <FormWrapperModal
        className="job-offer-detail-modal"
        open={EditViewOpen}
        onClose={(event, reason) => {
          if (reason && reason === 'backdropClick') return;
          setEditViewOpen(false);
        }}
      >
        <div className="job-offer-detail-modal__body">
          <ManageOffer offerId={offer.id} closeHandler={() => setEditViewOpen(false)} />
        </div>
      </FormWrapperModal>
      <Grid
        container
        className={clsx('manage-job-offer', {
          'manage-job-offer--inactive': !offer.isActive
        })}
        component={Paper}
        elevation={2}
      >
        <Grid item xs="auto" className="centered">
          <Fab onClick={() => setMenuOpen(true)} size="small" className="action" ref={anchorRef}>
            <Menu />
          </Fab>
          <ContextMenu open={menuOpen} setOpen={setMenuOpen} anchorEl={anchorRef.current}>
            <MenuItem
              onClick={() => {
                setMenuOpen(false);
                setEditViewOpen(true);
              }}
            >
              {t(translations.pages.manageJobOffers.edit())}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuOpen(false);
                setDeleteModalOpen(true);
              }}
            >
              {t(translations.pages.manageJobOffers.delete())}
            </MenuItem>
            <Link to={`/companies/${offer.company.id}`}>
              <MenuItem>{t(translations.pages.manageCompanies.companyProfile())}</MenuItem>
            </Link>
            <Link
              to={{
                pathname: `/jobs/${offer.id}`
              }}
              state={{ from: window.location.pathname }}
            >
              <MenuItem>{t(translations.pages.manageJobOffers.jobOfferView())}</MenuItem>
            </Link>
          </ContextMenu>
        </Grid>
        <Grid item xs={3} className="manage-job-offer__title">
          {offer.title}
        </Grid>
        <Grid item xs className="manage-job-offer__company">
          <LabeledInput label={t(translations.common.company())} wrap>
            {offer.company.name}
          </LabeledInput>
        </Grid>
        <Grid item xs className="manage-job-offer__">
          <LabeledInput label={t(translations.common.type())}>{offer.jobType.name}</LabeledInput>
        </Grid>
        <Grid item xs className="manage-job-offer__">
          <LabeledInput label={t(translations.pages.jobOffers.createdAt())}>
            {new Date(offer.createdAt).toLocaleDateString()}
          </LabeledInput>
        </Grid>
        <Grid item xs className="manage-job-offer__">
          <LabeledInput label={t(translations.pages.jobOffers.activatedAt())}>
            {offer.activatedAt ? new Date(offer.activatedAt).toLocaleDateString() : '------'}
          </LabeledInput>
        </Grid>
        <Grid item xs={1} className="manage-job-offer__">
          <LabeledInput label={t(translations.pages.manageJobOffers.paid())}>
            {offer.jobType.paid ? t(translations.common.yes()) : t(translations.common.no())}
          </LabeledInput>
        </Grid>
      </Grid>
      <ConfirmationDialog
        open={deleteModalOpen}
        title={t(translations.pages.manageJobOffers.deleteConfirm.title())}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
        onConfirm={() => {
          setDeleteModalOpen(false);
          handleDelete();
        }}
      >
        <div>{t(translations.pages.manageJobOffers.deleteConfirm.message())}</div>
      </ConfirmationDialog>
    </>
  );
};
